import React, { useEffect, useState } from 'react';
import {
  Col, Row, Badge, Typography, Icon, Tabs, Spin, Skeleton,
} from 'antd';
import { Link, withRouter } from 'react-router-dom';
import * as PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';
import routeLocations, { externalRoutes } from '../../../../utility/route-locations';
import DashGraphCreditBurn from './DashGraphCreditBurn';
import DashTableIssues from './DashTableIssues';
import DashGraphInspectionRate from './DashGraphInspectionRate';
import DashGraphInspectionBreakdown from './DashGraphInspectionBreakdown';
import DashTableLatestInspections from './DashTableLatestInspections';
import { LogoLocations, ViaLogo } from '../../../../components/logo/ViaLogo';
import useWindowSize from '../../../../utility/hooks/useWindowSize';

const { Title } = Typography;
const { TabPane } = Tabs;


// Graphical widgets
const GraphPane = withRouter(({
  history, dashboardData, isLoading, isRateDataLoading, isReasonDataLoading, loadRateData, loadReasonData, sessionUser, widgetSettings, updateWidgetNumbers,
}) => {
  const [tabIndex, setTabIndex] = useState('1');
  const [otherIndex, setOtherIndex] = useState('1');

  useEffect(() => {
    if (widgetSettings && widgetSettings.length > 0) {
      setTabIndex(`${widgetSettings[0]}`);
      setOtherIndex(`${widgetSettings[1]}`);
    }
  }, [widgetSettings, setTabIndex]);

  return (
    <div className="via-dashboard-widget main">
      <Skeleton active paragraph={{ rows: 11 }} loading={isLoading}>
        <Tabs
          defaultActiveKey={tabIndex}
          onChange={(left) => { updateWidgetNumbers(+left, +otherIndex, widgetSettings[2], widgetSettings[3], widgetSettings[4]); }}
        >
          <TabPane tab={<FormattedMessage id="widgetCreditBurn" />} key="1">
            <Row align="top" justify="center" type="flex">
              <DashGraphCreditBurn history={history} dashboardData={dashboardData} sessionUser={sessionUser} />
            </Row>
          </TabPane>
          <TabPane tab={<FormattedMessage id="widgetInspectionRate" />} key="2">
            <Row align="top" justify="center" type="flex">
              <DashGraphInspectionRate
                history={history}
                dashboardData={dashboardData}
                sessionUser={sessionUser}
                changeData={loadRateData}
                localLoading={isRateDataLoading}
                widgetSettings={widgetSettings}
                updateWidgetNumbers={updateWidgetNumbers}
              />
            </Row>
          </TabPane>
          <TabPane tab={<FormattedMessage id="widgetInspectionBreakdown" />} key="3">
            <Row align="top" justify="center" type="flex">
              <DashGraphInspectionBreakdown
                history={history}
                dashboardData={dashboardData}
                changeData={loadReasonData}
                localLoading={isReasonDataLoading}
                widgetSettings={widgetSettings}
                updateWidgetNumbers={updateWidgetNumbers}
              />
            </Row>
          </TabPane>
        </Tabs>
      </Skeleton>
    </div>
  );
});

// Table widgets
const TablePane = withRouter(({
  history, dashboardData, isLoading, isIssueDataLoading, loadIssueData, widgetSettings, updateWidgetNumbers,
}) => {
  const [tabIndex, setTabIndex] = useState('1');
  const [otherIndex, setOtherIndex] = useState('1');
  useEffect(() => {
    if (widgetSettings && widgetSettings.length > 0) {
      setTabIndex(`${widgetSettings[1]}`);
      setOtherIndex(`${widgetSettings[0]}`);
    }
  }, [widgetSettings, setTabIndex]);
  return (
    <div className="via-dashboard-widget main">
      <Skeleton active paragraph={{ rows: 11 }} loading={isLoading}>
        <Tabs
          defaultActiveKey={tabIndex}
          onChange={(right) => { updateWidgetNumbers(+otherIndex, +right, widgetSettings[2], widgetSettings[3], widgetSettings[4]); }}
        >
          <TabPane tab={<FormattedMessage id="widgetIssues" />} key="1">
            <Row align="top" justify="center" type="flex">
              <DashTableIssues
                history={history}
                dashboardData={dashboardData}
                changeData={loadIssueData}
                localLoading={isIssueDataLoading}
                widgetSettings={widgetSettings}
                updateWidgetNumbers={updateWidgetNumbers}
              />
            </Row>
          </TabPane>
          <TabPane tab={<FormattedMessage id="widgetLatestInspections" />} key="2">
            <Row align="top" justify="center" type="flex">
              <DashTableLatestInspections history={history} dashboardData={dashboardData} />
            </Row>
          </TabPane>
        </Tabs>
      </Skeleton>
    </div>
  );
});

// STATUS Messages and Stats
function StatusPane({ dashboardData, isLoading }) {
  const intl = useIntl();

  let logoImageData = null;
  let logoDataType = null;
  let groupCompanyName = 'My Company';

  // Group details
  if (dashboardData != null && dashboardData.userGroup != null) {
    logoImageData = dashboardData.userGroup.logo;
    logoDataType = dashboardData.userGroup.logoType;
    groupCompanyName = dashboardData.userGroup.description;
  }

  // Stats
  const [stats, setStats] = useState([]);
  useEffect(() => {
    if (dashboardData) {
      const newStats = [];

      // Verified users (email verified)
      let verifiedUsers = 0;
      if (dashboardData.groupUsers && dashboardData.groupUsers.length > 0) {
        dashboardData.groupUsers.forEach(user => {
          verifiedUsers += user.active && user.emailVerified ? 1 : 0; // only count if active
        });
      }
      newStats.push({
        id: 'statusStatVerifiedUsers',
        icon: 'user',
        number: verifiedUsers,
      });

      // Vehicles and Inspection Summary
      let noVehicles = 0;
      let noDamagedVehicles = 0;
      let weeklyInspections = 0;
      let weeklyInspectors = 0;
      if (dashboardData.inspectionStatistics) {
        noVehicles = dashboardData.inspectionStatistics.noVehicles;
        noDamagedVehicles = dashboardData.inspectionStatistics.noVehiclesDamaged;
        weeklyInspections = dashboardData.inspectionStatistics.weekInspections;
        weeklyInspectors = dashboardData.inspectionStatistics.noInspectors;
      }
      newStats.push({
        id: 'statusStatVehicleTotal',
        icon: 'car',
        number: noVehicles,
      });
      newStats.push({
        id: 'statusStatVehicleDamage',
        icon: 'tool',
        number: noDamagedVehicles,
      });
      newStats.push({
        id: 'statusStatInspectWeek',
        icon: 'file-search',
        number: weeklyInspections,
      });
      newStats.push({
        id: 'statusStatInspectors',
        icon: 'solution',
        number: weeklyInspectors,
      });

      setStats(newStats);
    }
  }, [dashboardData]);

  // Messages
  const [messages, setMessages] = useState([]);
  useEffect(() => {
    if (dashboardData) {
      const newMessages = [];

      // Today's UTC date
      let todayDate = new Date();
      todayDate = new Date(todayDate.getUTCFullYear(), todayDate.getUTCMonth(), todayDate.getUTCDate());

      // Credit balance checks
      let creditBalance = 0;
      let creditsBeyond7Days = 0;
      if (dashboardData.packageSummaryList && dashboardData.packageSummaryList.length > 0) {
        dashboardData.packageSummaryList.forEach(summary => {
          creditBalance += summary.tokensRemaining;
          let expDate = new Date(summary.expiryDate);
          expDate = new Date(expDate.getUTCFullYear(), expDate.getUTCMonth(), expDate.getUTCDate());
          if ((+expDate - +todayDate) > (7 * 24 * 60 * 60 * 1000)) {
            creditsBeyond7Days += summary.tokensRemaining;
          }
        });
      }
      if (creditBalance < 50) {
        newMessages.push({
          id: 'statusMsgCreditBalance',
          type: 'warning',
          linkURL: routeLocations.purchaseCredits,
          linkId: 'widgetMessagePurchase',
        });
      }
      if (creditBalance >= 50 && creditsBeyond7Days < 1) {
        newMessages.push({
          id: 'statusMsgCreditExpiry',
          type: 'warning',
          linkURL: routeLocations.purchaseCredits,
          linkId: 'widgetMessagePurchase',
        });
      }

      // Setup incomplete
      if (dashboardData.userGroup == null || dashboardData.userGroup.description == null
      || dashboardData.userGroup.description === 'My Company'
      || dashboardData.userGroup.description === '' || dashboardData.userGroup.address == null
      || dashboardData.userGroup.address.line1 == null || dashboardData.userGroup.address.line1 === ''
      || dashboardData.userGroup.address.country == null || dashboardData.userGroup.address.country === ''
      || dashboardData.userGroup.currency == null || dashboardData.userGroup.currency.currencyCode == null
      || dashboardData.userGroup.currency.currencyCode === '') {
        newMessages.push({
          id: 'statusMsgCompanyDetails',
          type: 'warning',
          linkURL: routeLocations.configure_contact_details,
          linkId: 'update',
        });
      }

      // Unverified emails
      let unverifiedUsers = false;
      if (dashboardData.groupUsers && dashboardData.groupUsers.length > 0) {
        dashboardData.groupUsers.forEach(user => {
          unverifiedUsers = unverifiedUsers || !user.emailVerified; // look for unverifed emails across all users (active or not)
        });
      }
      if (unverifiedUsers) {
        newMessages.push({
          id: 'statusMsgUnverifiedEmails',
          type: 'warning',
          linkURL: routeLocations.configure_users,
          linkId: 'review',
        });
      }

      setMessages(newMessages);
    }
  }, [dashboardData]);

  return (
    <div className="via-dashboard-widget status">
      <div>
        <Row className="via-dashboard-status-top">
          <Spin spinning={isLoading} tip={<div className="via-dashboard-status-hide"><Spin /></div>}>
            <Row align="middle" type="flex" justify="center">
              { logoImageData && logoDataType
                ? <img src={`data:${logoDataType};base64,${logoImageData}`} className="via-company-logo dashboard" alt="" />
                : <ViaLogo className="via-footer-logo" logoSrc={LogoLocations.MOBLYX_NORMAL_BLUE_CIRCLE_WHITE_M} href={externalRoutes.moblyx} />}
            </Row>
            <Row align="middle" type="flex" justify="center">
              <Title level={4} className="via-dashboard-status-company-name">{groupCompanyName}</Title>
            </Row>
            { stats && stats.length > 0
              ? stats.map(stat => (
                <Row key={Math.random()} align="middle" type="flex" style={{ marginTop: '0.3em' }}>
                  <Row align="middle" justify="start" type="flex" className="via-dashboard-widget-status-text">
                    <Col>
                      <Icon type={stat.icon} />
                    &nbsp;
                      <b>{stat.number}</b>
                    &nbsp;
                      <FormattedMessage
                        id={stat.id}
                        values={{
                          endS: stat.number !== 1 ? intl.formatMessage({ id: 'pluralLetter' }) : '',
                        }}
                      />
                    </Col>
                  </Row>
                </Row>
              ))
              : '' }
          </Spin>
        </Row>
        <Row className="via-dashboard-status-bottom">
          <Skeleton active loading={isLoading}>
            <Row align="middle" justify="space-between" type="flex">
              <span className="via-dashboard-widget-title status"><FormattedMessage id="widgetStatusTitle" /></span>
            </Row>
            <Row align="top" justify="start" type="flex">
              <table>
                <tbody>
                  { messages && messages.length > 0
                    ? messages.map(msg => (
                      <tr key={Math.random()}>
                        <td>
                          <Badge
                            key={Math.random()}
                            status={msg.type}
                            text={(
                              <span>
                                <FormattedMessage id={msg.id} />
                                <Link to={msg.linkURL} style={{ marginLeft: '0.3em' }} ><FormattedMessage id={msg.linkId} /></Link>
                              </span>
                            )}
                          />
                        </td>
                      </tr>
                    ))
                    : (<tr><td><Badge color="var(--component-primary-colour)" text={<FormattedMessage id="statusMsgNoMessages" />} /></td></tr>)}
                </tbody>
              </table>
            </Row>
          </Skeleton>
        </Row>
      </div>
    </div>
  );
}

// Layout
function DashboardSummaries({
  dashboardData, isLoading, isRateDataLoading, isReasonDataLoading, isIssueDataLoading, loadRateData, loadReasonData, loadIssueData, sessionUser, widgetSettings, updateWidgetNumbers,
}) {
  const size = useWindowSize();
  return (
    <div className="summary-list">
      <Col order={1} xs={24} sm={24} md={12} lg={12} xl={9} style={{ maxWidth: '600px' }}>
        <GraphPane
          dashboardData={dashboardData}
          isLoading={isLoading}
          isRateDataLoading={isRateDataLoading}
          isReasonDataLoading={isReasonDataLoading}
          loadRateData={loadRateData}
          loadReasonData={loadReasonData}
          sessionUser={sessionUser}
          widgetSettings={widgetSettings}
          updateWidgetNumbers={updateWidgetNumbers}
        />
      </Col>
      <Col order={size[0] < 768 ? 0 : 2} xs={24} sm={24} md={12} lg={12} xl={9} style={{ maxWidth: '600px' }}>
        <TablePane
          dashboardData={dashboardData}
          isLoading={isLoading}
          isIssueDataLoading={isIssueDataLoading}
          loadIssueData={loadIssueData}
          widgetSettings={widgetSettings}
          updateWidgetNumbers={updateWidgetNumbers}
        />
      </Col>
      <Col order={3} xs={24} sm={24} md={12} lg={12} xl={6} style={{ maxWidth: '600px' }}>
        <StatusPane
          dashboardData={dashboardData}
          isLoading={isLoading}
        />
      </Col>
    </div>
  );
}

DashboardSummaries.propTypes = {
  dashboardData: PropTypes.oneOfType([PropTypes.object]),
  isLoading: PropTypes.bool.isRequired,
  isRateDataLoading: PropTypes.bool.isRequired,
  isReasonDataLoading: PropTypes.bool.isRequired,
  isIssueDataLoading: PropTypes.bool.isRequired,
  loadRateData: PropTypes.func.isRequired,
  loadReasonData: PropTypes.func.isRequired,
  loadIssueData: PropTypes.func.isRequired,
  sessionUser: PropTypes.shape({
    displayName: PropTypes.string.isRequired,
  }).isRequired,
  widgetSettings: PropTypes.arrayOf(PropTypes.number),
  updateWidgetNumbers: PropTypes.func,
};

DashboardSummaries.defaultProps = {
  dashboardData: null,
  widgetSettings: [1, 1],
  updateWidgetNumbers: () => {},
};

export default DashboardSummaries;
